import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTheme, useTokens } from '../theme';
import { notifyError, notifySuccess } from '../toast';
import {
  listAdmins, inviteAdmin, revokeInstitutionAdmin,
} from '../services/institutions';
import {
  Card, SectionTitle, Button, Field, Row, StatusBadge, ErrorState,
} from '../uiKit';
import { Toggle } from '../Toggle';
import { Modal } from '../Modal.jsx';
import { RowsSkeleton } from '../Skeleton.jsx';

const ALL_SCOPES = [
  'ANALYTICS_VIEW',
  'SEATS_MANAGE',
  'BRANDING_MANAGE',
  'ADMINS_MANAGE',
  'BILLING_VIEW',
];
const SCOPE_LABEL = {
  ANALYTICS_VIEW: 'Analytics',
  SEATS_MANAGE: 'Seats',
  BRANDING_MANAGE: 'Branding',
  ADMINS_MANAGE: 'Admins',
  BILLING_VIEW: 'Billing',
};
const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'OWNER', label: 'Owner' },
];
const SCOPE_OPTIONS = ALL_SCOPES.map((s) => { return { value: s, label: SCOPE_LABEL[s] }; });

/**
 * List + invite + revoke institution admins. `institutionId` scopes it for a
 * super-admin; omit it and an institution admin manages their own school.
 * Super-admins may override the verified-domain gate, so that exception is
 * surfaced before the invite is sent instead of happening silently.
 *
 * Revoking asks first, since it takes effect immediately. Pass `currentEmail`
 * (the signed-in admin) to hide the revoke action on your own row.
 *
 * `viewer` ({ role, scopes, superAdmin }) decides who may resend an expired
 * invite, mirroring the API's invite rules: only an owner or super-admin can
 * re-invite an owner, and an admin only with every permission on the row.
 */
export default function AdminsPanel({
  institutionId, verifiedDomains = [], currentEmail = '', viewer = {},
}) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  const [admins, setAdmins] = useState([]);
  // Where focus lands after a revoke: the dialog's opener row is gone by then.
  const inviteHeadingRef = useRef(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('ADMIN');
  const [scopes, setScopes] = useState(['ANALYTICS_VIEW']);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const selfEmail = String(currentEmail || '').trim().toLowerCase();
  const adminHasNoPermissions = role === 'ADMIN' && scopes.length === 0;
  const viewerIsOwner = Boolean(viewer.superAdmin || viewer.role === 'OWNER');
  const canResend = (admin) => {
    if (viewerIsOwner) { return true; }
    if (admin.role === 'OWNER') { return false; }
    return (admin.scopes || []).every((scope) => { return (viewer.scopes || []).includes(scope); });
  };

  const inviteeDomain = (() => {
    const normalized = email.trim().toLowerCase();
    const at = normalized.lastIndexOf('@');
    return at > 0 && at < normalized.length - 1 ? normalized.slice(at + 1) : '';
  })();
  const normalizedDomains = (verifiedDomains || [])
    .map((domain) => { return String(domain || '').trim().toLowerCase(); })
    .filter(Boolean);
  const usesDomainOverride = Boolean(
    institutionId
    && inviteeDomain
    && !normalizedDomains.includes(inviteeDomain),
  );

  const load = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) { setLoading(true); }
    try {
      const data = await listAdmins(institutionId);
      setAdmins(data.admins || []);
      setLoadFailed(false);
    } catch (err) {
      setAdmins([]);
      setLoadFailed(true);
    } finally {
      if (showLoading) { setLoading(false); }
    }
  }, [institutionId]);
  useEffect(() => { load(); }, [load]);

  const visibleAdmins = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return admins.filter((admin) => {
      if (admin.status === 'REVOKED') { return false; }
      if (!normalizedQuery) { return true; }
      const searchable = [
        admin.email,
        admin.role,
        admin.status,
        ...(admin.scopes || []).map((scope) => { return SCOPE_LABEL[scope] || scope; }),
      ].join(' ').toLowerCase();
      return searchable.includes(normalizedQuery);
    });
  }, [admins, query]);

  const invitedAdmins = useMemo(() => {
    return visibleAdmins
      .filter((admin) => { return admin.status === 'INVITED'; })
      .sort((a, b) => { return new Date(b.createdAt || 0) - new Date(a.createdAt || 0); });
  }, [visibleAdmins]);

  const existingAdmins = useMemo(() => {
    return visibleAdmins
      .filter((admin) => { return admin.status !== 'INVITED'; })
      .sort((a, b) => { return String(a.email || '').localeCompare(String(b.email || '')); });
  }, [visibleAdmins]);

  // A re-invite replaces that email's row, so an expired invite does not linger.
  const showInvited = async (result) => {
    if (result?.admin) {
      setAdmins((current) => {
        const invitedEmail = String(result.admin.email || '').toLowerCase();
        return [
          ...current.filter((admin) => {
            return String(admin.email || '').toLowerCase() !== invitedEmail;
          }),
          result.admin,
        ];
      });
    } else {
      await load({ showLoading: false });
    }
  };

  const invite = async () => {
    if (!email.trim()) {
      notifyError('Enter an admin email.');
      return;
    }
    if (adminHasNoPermissions) {
      notifyError('Choose at least one permission for this admin.');
      return;
    }
    setBusy(true);
    try {
      const result = await inviteAdmin({
        email: email.trim(),
        role,
        scopes: role === 'OWNER' ? ALL_SCOPES : scopes,
      }, institutionId);
      await showInvited(result);
      setEmail('');
      setRole('ADMIN');
      setScopes(['ANALYTICS_VIEW']);
      notifySuccess('Admin invitation sent.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Failed to send invite.');
    } finally {
      setBusy(false);
    }
  };

  // Resending keeps the row's own role and permissions; the form's defaults
  // would silently turn an owner invite into Admin + Analytics.
  const resend = async (admin) => {
    setResendingId(admin._id);
    try {
      const result = await inviteAdmin({
        email: admin.email,
        role: admin.role,
        scopes: admin.role === 'OWNER' ? ALL_SCOPES : (admin.scopes || []),
      }, institutionId);
      await showInvited(result);
      notifySuccess('Admin invitation sent.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Failed to send invite.');
    } finally {
      setResendingId(null);
    }
  };

  const changeInviteRole = (nextRole) => {
    setRole(nextRole);
    setScopes(nextRole === 'OWNER' ? ALL_SCOPES : ['ANALYTICS_VIEW']);
  };

  const revoke = async (admin) => {
    const adminId = admin._id;
    const invited = admin.status === 'INVITED';
    setRevoking(true);
    try {
      await revokeInstitutionAdmin(adminId, institutionId);
      setAdmins((current) => {
        return current.filter((row) => { return String(row._id) !== String(adminId); });
      });
      notifySuccess(invited ? 'Invite cancelled.' : 'Admin access revoked.');
      setPendingRevoke(null);
      // After the dialog's own close effect, which targets the removed row.
      setTimeout(() => { inviteHeadingRef.current?.focus(); }, 0);
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Failed to revoke admin access.');
    } finally {
      setRevoking(false);
    }
  };

  const renderAdmin = (admin, pending) => {
    // An invite link stops working after its TTL; re-inviting the same email
    // issues a new one, which Resend does with the row's role and permissions.
    const expired = pending && admin.expiresAt && new Date(admin.expiresAt) < new Date();
    return (
      <Row
        key={admin._id}
        left={(
          <div>
            <span className="text-sm block" style={{ fontFamily: t.fonts.mono, color: t.body }}>
              {admin.email}
            </span>
            <span className="text-sm" style={{ fontFamily: t.fonts.body, color: t.muted }}>
              {admin.role === 'OWNER' ? 'Owner' : 'Admin'} · {(admin.scopes || []).map((s) => { return SCOPE_LABEL[s] || s; }).join(', ') || 'No permissions'}
            </span>
            {expired && !canResend(admin) ? (
              <span className="text-sm block" style={{ fontFamily: t.fonts.body, color: t.muted }}>
                Invite expired. Ask an owner to send a new one.
              </span>
            ) : null}
            {expired && canResend(admin) ? (
              <>
                <span className="text-sm block" style={{ fontFamily: t.fonts.body, color: t.muted }}>
                  Invite expired. Resend it with the same role and permissions.
                </span>
                <button
                  type="button"
                  onClick={() => { resend(admin); }}
                  disabled={resendingId !== null}
                  aria-label={`Resend invite to ${admin.email}`}
                  className={`-ml-2 text-sm min-h-[44px] px-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
                  style={{ fontFamily: t.fonts.body, color: t.heading }}
                >
                  {resendingId === admin._id ? 'Sending…' : 'Resend'}
                </button>
              </>
            ) : null}
          </div>
        )}
        right={(
          <div className="flex items-center gap-3">
            <StatusBadge label={expired ? 'Expired' : (pending ? 'Invited' : 'Active')} />
            {selfEmail && String(admin.email || '').toLowerCase() === selfEmail ? null : (
              <button
                type="button"
                onClick={() => { setPendingRevoke(admin); }}
                aria-label={`${pending ? 'Cancel invite for' : 'Revoke'} ${admin.email}`}
                className={`text-sm min-h-[44px] px-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
                style={{ fontFamily: t.fonts.body, color: t.danger }}
              >
                {pending ? 'Cancel invite' : 'Revoke'}
              </button>
            )}
          </div>
        )}
      />
    );
  };

  const emptyMessage = query.trim()
    ? 'No people match your search.'
    : 'No administrators in this section yet.';

  return (
    <Card>
      {/* The portal page h1 already reads "Admins"; the console drill-in keeps it. */}
      <SectionTitle hideTitle={portal}>Admins</SectionTitle>

      <p ref={inviteHeadingRef} tabIndex={-1} className="mb-2 text-sm font-semibold focus:outline-none" style={{ fontFamily: t.fonts.body, color: t.heading }}>
        Invite an admin
      </p>
      <Field value={email} onChangeText={setEmail} placeholder="admin@school.edu" type="email" inputMode="email" />

      {usesDomainOverride ? (
        <div
          className="mb-3 rounded-md border px-3 py-2 text-sm"
          role="status"
          style={{
            backgroundColor: t.statBg,
            borderColor: t.border,
            color: t.heading,
            fontFamily: t.fonts.body,
          }}
        >
          {normalizedDomains.length
            ? `This email is outside the verified ${normalizedDomains.join(' or ')} domain${normalizedDomains.length > 1 ? 's' : ''}. Sending will use the super-admin override.`
            : 'This institution has no verified domains. Sending will use the super-admin override.'}
        </div>
      ) : null}

      <div className="mb-2">
        <Toggle label="Role" options={ROLE_OPTIONS} value={role} onChange={changeInviteRole} />
      </div>

      {role === 'OWNER' ? (
        <div
          className="mb-4 rounded-md border px-3 py-3 text-sm"
          style={{ backgroundColor: t.statBg, borderColor: t.border, color: t.body }}
        >
          Owners have full access to analytics, seats, administrators, branding, and billing.
        </div>
      ) : (
        <div className="mb-3">
          <Toggle label="Permissions" multiple options={SCOPE_OPTIONS} value={scopes} onChange={setScopes} />
          <p className="mt-1 text-sm" style={{ color: adminHasNoPermissions ? t.danger : t.muted }}>
            {adminHasNoPermissions
              ? 'Choose at least one permission. An admin with no permissions has no role in the portal.'
              : 'Admins can access only the areas selected here.'}
          </p>
        </div>
      )}

      <Button
        label={busy ? 'Sending…' : 'Send invite'}
        onClick={invite}
        disabled={busy || !email.trim() || adminHasNoPermissions}
      />

      <div className="mt-7 border-t pt-6" style={{ borderColor: t.hairline }}>
        <label className="block">
          <span
            className="mb-2 block text-sm font-semibold"
            style={{ fontFamily: t.fonts.body, color: t.heading }}
          >
            Search people
          </span>
          <Field
            value={query}
            onChangeText={setQuery}
            placeholder="Search by email, role, or permission"
            className="mb-0"
          />
        </label>
      </div>

      {!loading && loadFailed ? (
        <ErrorState className="mt-5" message="Couldn’t load your admin team." onRetry={() => { load(); }} />
      ) : null}
      {loading ? <div className="mt-5"><RowsSkeleton rows={4} /></div> : !loadFailed && (
        <div className="mt-6">
          <section aria-labelledby="invited-admins-heading">
            <div className="mb-1 flex items-center gap-2">
              <h4
                id="invited-admins-heading"
                className="text-sm font-semibold"
                style={{ fontFamily: t.fonts.body, color: t.heading }}
              >
                Invited
              </h4>
              <span className="text-xs" style={{ fontFamily: t.fonts.mono, color: t.muted }}>
                {invitedAdmins.length}
              </span>
            </div>
            {invitedAdmins.length ? invitedAdmins.map((admin) => { return renderAdmin(admin, true); }) : (
              <p className="py-3 text-sm" style={{ fontFamily: t.fonts.body, color: t.muted }}>{emptyMessage}</p>
            )}
          </section>

          <section className="mt-6" aria-labelledby="existing-admins-heading">
            <div className="mb-1 flex items-center gap-2">
              <h4
                id="existing-admins-heading"
                className="text-sm font-semibold"
                style={{ fontFamily: t.fonts.body, color: t.heading }}
              >
                Existing admins
              </h4>
              <span className="text-xs" style={{ fontFamily: t.fonts.mono, color: t.muted }}>
                {existingAdmins.length}
              </span>
            </div>
            {existingAdmins.length ? existingAdmins.map((admin) => { return renderAdmin(admin, false); }) : (
              <p className="py-3 text-sm" style={{ fontFamily: t.fonts.body, color: t.muted }}>{emptyMessage}</p>
            )}
          </section>
        </div>
      )}

      <RevokeDialog
        admin={pendingRevoke}
        busy={revoking}
        onCancel={() => { if (!revoking) { setPendingRevoke(null); } }}
        onConfirm={() => { return revoke(pendingRevoke); }}
      />
    </Card>
  );
}

// Confirm step for a revoke. The dismiss button comes first so it takes the
// initial focus; a stray Enter never removes anyone.
function RevokeDialog({
  admin, busy, onCancel, onConfirm,
}) {
  const t = useTokens();
  const invited = admin?.status === 'INVITED';
  return (
    <Modal open={Boolean(admin)} onClose={onCancel} title={invited ? 'Cancel invite' : 'Remove access'}>
      <p className="text-base" style={{ color: t.body, fontFamily: t.fonts.body }}>
        {invited
          ? `Cancel the invite for ${admin?.email}? The invite link stops working immediately.`
          : `Remove ${admin?.email}'s portal access? They lose access immediately.`}
      </p>
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button small variant="secondary" label={invited ? 'Keep invite' : 'Cancel'} onClick={onCancel} disabled={busy} />
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          aria-busy={busy || undefined}
          className={`inline-flex items-center justify-center rounded-[10px] min-h-[44px] px-4 text-sm font-medium border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${busy ? 'opacity-55 cursor-not-allowed' : ''}`}
          style={{ color: t.danger, borderColor: t.danger, backgroundColor: 'transparent', fontFamily: t.fonts.body }}
        >
          {invited ? 'Cancel invite' : 'Remove access'}
        </button>
      </div>
    </Modal>
  );
}
